import { flattenResourceTree, PREVIEW_SAMPLE_RESOURCES } from './permission-helpers'

// Matrix 的 resource tree 展開/收合 + indexing 邏輯（UserPermissionMatrix / RoleMatrix 共用）
//
// 提供：
//   props: resources / previewMode
//   data:  collapsedKeys (Set)
//   computed:
//     - displayResources: 依 previewMode 是否注入 PREVIEW 示範資源
//     - flatNodes: tree 展平（含 depth / isLeaf / childKeys）
//     - visibleNodes: 依 collapsedKeys 過濾後的展示序列
//     - resourceByKey: key → resource 的 Map（取代每次 O(N) .find）
//     - childrenByParent: parentKey → childKey[] 的 Map（供 descendantKeysCached 使用）
//   methods:
//     - isCollapsed(key) / toggleCollapse(key)
//     - descendantKeysCached(parentKey): 用 childrenByParent 做的 cached descendants 遍歷
export default {
  props: {
    resources: { type: Array, default: () => [] },
    previewMode: { type: Boolean, default: false },
    // Opt-in：UserPermissionMatrix 由 UsersView 控；RoleMatrix 暫不暴露此 UI，
    // 不傳 = default false = 行為與舊版完全一致。改 default 前須同步檢視 RoleMatrix。
    hideLegacyResources: { type: Boolean, default: false }
  },
  data () {
    return {
      collapsedKeys: new Set()
    }
  },
  computed: {
    displayResources () {
      const base = this.hideLegacyResources
        ? this.resources.filter(r => !r.useLegacyPermission)
        : this.resources
      return this.previewMode
        ? [...base, ...PREVIEW_SAMPLE_RESOURCES]
        : base
    },
    flatNodes () {
      return flattenResourceTree(this.displayResources)
    },
    visibleNodes () {
      const hidden = new Set()
      for (const node of this.flatNodes) {
        const pk = node.resource.parentKey
        if (pk && (hidden.has(pk) || this.collapsedKeys.has(pk))) {
          hidden.add(node.resource.key)
        }
      }
      return this.flatNodes.filter(n => !hidden.has(n.resource.key))
    },
    resourceByKey () {
      const map = new Map()
      for (const r of this.displayResources) map.set(r.key, r)
      return map
    },
    childrenByParent () {
      const map = new Map()
      for (const r of this.displayResources) {
        if (!r.parentKey) continue
        if (!map.has(r.parentKey)) map.set(r.parentKey, [])
        map.get(r.parentKey).push(r.key)
      }
      return map
    }
  },
  methods: {
    isCollapsed (key) {
      return this.collapsedKeys.has(key)
    },
    toggleCollapse (key) {
      const next = new Set(this.collapsedKeys)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      this.collapsedKeys = next
    },
    // 用 childrenByParent map 取代 helpers.descendantKeys 的線性掃描；
    // 避免 cellState / broadcastCell 熱路徑每次重建 childrenMap
    descendantKeysCached (parentKey) {
      const result = []
      const visited = new Set()
      const stack = [...(this.childrenByParent.get(parentKey) || [])]
      while (stack.length > 0) {
        const k = stack.pop()
        if (visited.has(k)) continue
        visited.add(k)
        result.push(k)
        const kids = this.childrenByParent.get(k) || []
        for (const c of kids) stack.push(c)
      }
      return result
    }
  }
}
